import { ActionMenu } from '@components/ActionMenu';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { useCurrentProject } from '@modules/projects/utils/useCurrentProject';
import { Stop } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem, Stack } from '@mui/material';
import { useNotification } from '@utils/useNotification';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { WorkFlowStatus, WorkflowInstance, useActionsOfWorkflowInstanceQuery, useStopWorkflowInstanceMutation, useWorkflowInstanceQuery, useWorkflowInstancesQuery } from 'gql/index';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface Props {
  projectWorkflow: Pick<WorkflowInstance, 'id' | 'workFlowStatus'>;
}

export const WorkflowInstanceActions: React.FC<Props> = ({ projectWorkflow }) => {
  const { formatMessage } = useIntl();
  const { notifySuccess } = useNotification();
  const invalidateQuery = useQueryInvalidator();

  const { projectId, isArchived, canManageWorkflows } = useCurrentProject();

  const { mutate: stopWorkflow, isPending: isDeleting } = useStopWorkflowInstanceMutation({
    onSuccess: () => {
      notifySuccess(formatMessage({ id: 'Workflow successfully stopped.' }));
      invalidateQuery(useWorkflowInstanceQuery, { projectId, workflowId: projectWorkflow.id });
      invalidateQuery(useWorkflowInstancesQuery, { projectId });
      invalidateQuery(useActionsOfWorkflowInstanceQuery, { workflowId: projectWorkflow.id });
    }
  });

  const onDeleteConfirm = () => {
    stopWorkflow({ input: { id: projectWorkflow.id, projectId: projectId } });
  };

  const [confirmRemoveDialogOpen, setConfirmRemoveDialogOpen] = useState(false);
  const [moreMenuDrawerOpen, setMoreMenuDrawerOpen] = useState(false);

  if (!canManageWorkflows || projectWorkflow.workFlowStatus !== WorkFlowStatus.InProgress) {
    return undefined;
  }

  return (
    <>
      <Stack direction='row' gap={2}>
        <ActionMenu open={moreMenuDrawerOpen} onOpen={setMoreMenuDrawerOpen}>
          <MenuItem onClick={() => setConfirmRemoveDialogOpen(true)} disabled={isArchived}>
            <ListItemIcon><Stop /></ListItemIcon>
            <ListItemText primary={formatMessage({ id: 'Stop' })} />
          </MenuItem>
        </ActionMenu>
      </Stack>

      <ConfirmDialog
        open={confirmRemoveDialogOpen}
        title={formatMessage({ id: 'Stop workflow' })}
        content={formatMessage({ id: 'This workflow will be stopped. It will no longer be accessible to external users. This action is irreversible, are you sure?' })}
        confirmText={formatMessage({ id: 'Stop' })}
        confirmColor='error'
        onCancel={() => setConfirmRemoveDialogOpen(false)}
        onConfirm={onDeleteConfirm}
        loading={isDeleting}
      />
    </>
  );
};
